// Дерево прайса: чистые функции без React.
//
// На сервере правила лежат плоским списком (price_rules), дерево — только
// представление. Форма дерева отдельно НЕ хранится: она восстанавливается из
// самих правил. Узел считается разбитым по атрибуту, если есть правила, которые
// уточняют его этим атрибутом. Следствие, о котором стоит знать: разбиение, под
// которым не вписали ни одной цены, после перезагрузки исчезнет — оно и не несёт
// никакой информации.
//
// Семантика значения в правиле (та же, что на бэке — docs/price-lists.md):
//   null              — атрибут не участвует, «любой»
//   0 / пустая строка — атрибут у машины не заполнен, ветка «не указан»
//   значение          — точное совпадение

// Порядок только для разрешения ничьих при восстановлении дерева;
// разбивать можно в любом порядке
export const ATTRS = [
  { key: 'issueYear', title: 'год' },
  { key: 'complectationId', title: 'комплектация' },
  { key: 'engineVolume', title: 'объём' },
  { key: 'wdId', title: 'привод' },
];

const ATTR_KEYS = ATTRS.map((a) => a.key);

export const NOT_SET = 'не указан';

// Тот же разбор, что и на бэке (PriceListService.NormalizeEngineVolume): сток
// хранит объём как попало — «1975», «2», «2,0», «1.4 TSI». Без него ветка,
// заведённая вручную как «2500», не совпала бы ни с одной машиной и показывала
// «нет в стоке», хотя машины есть — расхождение уходило только после сохранения
export const normalizeEngineVolume = (raw) => {
  if (raw == null || String(raw).trim() === '') return '';
  const m = String(raw).replace(',', '.').match(/\d+(\.\d+)?/);
  if (!m) return '';
  let v = Number(m[0]);
  if (!Number.isFinite(v) || v <= 0) return '';
  // см³ пишут целым числом от 100 и выше — переводим в литры
  if (v >= 100) v = Math.round((v / 1000) * 10) / 10;
  return v <= 0 ? '' : v.toFixed(1);
};

// Атрибуты, у которых «не заполнено» кодируется нулём, а не пустой строкой
const NUMERIC_ATTRS = ['complectationId', 'wdId'];

const isUnset = (key, value) => (NUMERIC_ATTRS.includes(key) ? !value : !String(value ?? '').length);

// Ветка стока подходит узлу, если совпали все зафиксированные атрибуты
export const groupMatches = (fixed, group) =>
  Object.keys(fixed).every((k) => String(group[k] ?? '') === String(fixed[k] ?? ''));

// Правило → объект только из заданных атрибутов (null отбрасываем: это «любой»)
export const ruleFixed = (rule) => {
  const out = {};
  if (rule.modelId !== null && rule.modelId !== undefined) out.modelId = rule.modelId;
  ATTR_KEYS.forEach((k) => {
    if (rule[k] !== null && rule[k] !== undefined) out[k] = rule[k];
  });
  return out;
};

const sameFixed = (a, b) => {
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  return ka.length === kb.length && ka.every((k) => String(a[k] ?? '') === String(b[k] ?? ''));
};

// Правило уточняет узел: согласовано по всем его атрибутам и задаёт что-то сверх
const extendsFixed = (ruleF, fixed) =>
  Object.keys(fixed).every((k) => String(ruleF[k] ?? '') === String(fixed[k] ?? '')) &&
  Object.keys(ruleF).length > Object.keys(fixed).length;

// По какому атрибуту узел был разбит: тот, которым его уточняет больше всего правил
const splitAttrFor = (fixed, rules) => {
  const votes = {};
  rules.forEach((r) => {
    const rf = ruleFixed(r);
    if (!extendsFixed(rf, fixed)) return;
    Object.keys(rf)
      .filter((k) => !(k in fixed) && ATTR_KEYS.includes(k))
      .forEach((k) => {
        votes[k] = (votes[k] || 0) + 1;
      });
  });
  const best = Object.keys(votes).sort(
    (a, b) => votes[b] - votes[a] || ATTR_KEYS.indexOf(a) - ATTR_KEYS.indexOf(b)
  );
  return best[0] || null;
};

// Значения ветки берутся из стока И из правил.
//
// Только из стока было нельзя: приказ регулярно называет вариант, которого в
// наличии ещё нет (новый привод, новая комплектация, следующий модельный год).
// Завести на него цену заранее — единственный способ не дать приехавшей машине
// молча унаследовать цену родителя. А раз правило есть, ветка обязана быть видна,
// иначе цена сохранилась бы, но пропала с экрана.
const distinctValues = (attr, groups, rules = [], fixed = {}) => {
  const seen = new Map();
  const blank = NUMERIC_ATTRS.includes(attr) ? 0 : '';
  const add = (v) => seen.set(String(v ?? ''), v ?? blank);

  groups.forEach((g) => add(g[attr]));
  rules.forEach((r) => {
    const rf = ruleFixed(r);
    if (!(attr in rf)) return;
    // правило должно принадлежать этой ветке, а не соседней
    if (!Object.keys(fixed).every((k) => String(rf[k] ?? '') === String(fixed[k] ?? ''))) return;
    add(rf[attr]);
  });

  // «не указан» всегда последним — это не значение, а дыра в данных
  return [...seen.values()].sort((a, b) => {
    const ua = isUnset(attr, a);
    const ub = isUnset(attr, b);
    if (ua !== ub) return ua ? 1 : -1;
    return String(a).localeCompare(String(b), 'ru', { numeric: true });
  });
};

export const makeNode = (fixed, stock, rules) => {
  const groups = stock.filter((g) => groupMatches(fixed, g));
  const own = rules.find((r) => sameFixed(ruleFixed(r), fixed));
  const by = splitAttrFor(fixed, rules);
  return {
    fixed,
    by,
    price: own ? own.price : null,
    groups,
    kids: by
      ? distinctValues(by, groups, rules, fixed).map((v) =>
          makeNode({ ...fixed, [by]: v }, stock, rules)
        )
      : [],
  };
};

// Верхний уровень всегда модель: приказы адресуют цену не мельче модели
export const buildTree = (stock, rules) =>
  distinctValues('modelId', stock, rules, {}).map((modelId) =>
    makeNode({ modelId }, stock, rules)
  );

export const splitNode = (node, attr, stock, rules) => ({
  ...node,
  by: attr,
  kids: distinctValues(attr, node.groups, rules, node.fixed).map((v) =>
    makeNode({ ...node.fixed, [attr]: v }, stock, rules)
  ),
});

// Ручная ветка: значение, которого в стоке ещё нет, но которое есть в приказе.
// Появляется как обычный узел с нулём машин и живёт до тех пор, пока в ней есть
// цена — правило без цены не сохраняется и после перезагрузки исчезнет само.
export const addBranch = (node, attr, value, stock, rules) => {
  const key = String(value ?? '');
  if (node.kids.some((k) => String(k.fixed[attr] ?? '') === key)) return node;
  const kid = makeNode({ ...node.fixed, [attr]: value }, stock, rules);
  return { ...node, by: attr, kids: [...node.kids, kid] };
};

export const collapseNode = (node) => ({ ...node, by: null, kids: [] });

// Ключи всех веток поддерева, включая саму. Нужны, чтобы клик раскрывал ветку
// целиком, а не по уровню за раз: цена нередко лежит на третьем уровне
// (Carnival → комплектация → объём), и до неё приходилось щёлкать трижды,
// каждый раз глядя на строки с прочерком вместо цифры
export const subtreePaths = (node) => {
  const out = [];
  const walk = (n) => {
    out.push(JSON.stringify(n.fixed));
    n.kids.forEach(walk);
  };
  walk(node);
  return out;
};

// Сколько цен потеряется при сворачивании — на это спрашиваем подтверждение
export const pricedInside = (node) =>
  node.kids.reduce((sum, k) => sum + (k.price != null ? 1 : 0) + pricedInside(k), 0);

// Дерево → плоский набор правил. Узлы без цены не хранятся вовсе.
export const treeToRules = (roots) => {
  const out = [];
  const walk = (n) => {
    if (n.price != null) {
      // Числовые атрибуты приводим явно: значения веток приходят из стока и могут
      // оказаться строками, а бэк ждёт int? — иначе сохранение падало бы в 400
      const num = (v) => (v ? Number(v) : 0);
      out.push({
        modelId: n.fixed.modelId ? Number(n.fixed.modelId) : null,
        issueYear: 'issueYear' in n.fixed ? String(n.fixed.issueYear ?? '') : null,
        complectationId: 'complectationId' in n.fixed ? num(n.fixed.complectationId) : null,
        engineVolume: 'engineVolume' in n.fixed ? String(n.fixed.engineVolume ?? '') : null,
        wdId: 'wdId' in n.fixed ? num(n.fixed.wdId) : null,
        price: n.price,
      });
    }
    n.kids.forEach(walk);
  };
  roots.forEach(walk);
  return out;
};

// Правила, которые дерево показать не может в принципе: верхний уровень — всегда
// модель, поэтому правило без modelId («на всю марку») в дереве не появится.
// Такие правила заводятся только SQL-скриптом, но раз они есть — сохранение не
// должно их молча удалять, ведь оно заменяет набор целиком.
// Правила с моделью сюда не попадают: их дерево строит из стока И из правил,
// поэтому они всегда представимы, и удаление ветки менеджером остаётся удалением.
export const unrepresentableRules = (rules) =>
  rules.filter((r) => r.modelId === null || r.modelId === undefined);

// Покрытие: цена наследуется вниз, поэтому считаем по листьям.
// `inherited` — цена, доставшаяся узлу от предков: для корней её нет, а для
// поддерева в середине дерева она обязательна, иначе ветка без своей цены
// выглядела бы непокрытой, хотя цена задана выше.
const walkStats = (n, inherited, acc) => {
  const price = n.price != null ? n.price : inherited;
  if (n.kids.length) {
    n.kids.forEach((k) => walkStats(k, price, acc));
    return;
  }
  n.groups.forEach((g) => {
    acc.total += g.count;
    if (price == null) return;
    acc.priced += g.count;
    // Считаем машины, а не ветки: по min/max можно было сказать только «внутри
    // ветки разброс», и число расходилось с предпросмотром применения
    let off = 0;
    Object.entries(g.prices || {}).forEach(([at, cnt]) => {
      if (Number(at) !== price) off += cnt;
    });
    acc.changing += off;
    // У ветки известно только общее число заблокированных, не их цены, поэтому
    // ограничиваем сверху числом машин, которые вообще расходятся с прайсом
    if (off) acc.locked += Math.min(g.locked || 0, off);
  });
};

const finish = (acc) => ({ ...acc, withoutPrice: acc.total - acc.priced });
const zero = () => ({ total: 0, priced: 0, changing: 0, locked: 0 });

export const coverage = (roots) => {
  const acc = zero();
  roots.forEach((n) => walkStats(n, null, acc));
  return finish(acc);
};

// То же самое, но по одной ветке — для значков на свёрнутой строке: сколько
// внутри машин без цены и сколько расходится с прайсом, не разворачивая её
export const subtreeStats = (node, inherited = null) => {
  const acc = zero();
  walkStats(node, inherited, acc);
  return finish(acc);
};

export const updateNode = (roots, target, patch) => {
  const walk = (n) =>
    n === target ? patch(n) : { ...n, kids: n.kids.map(walk) };
  return roots.map(walk);
};
